// Lista de pedidos con filtros por dia / semana / mes / rango y por cliente.
// Es la unica pantalla donde se puede ANULAR: anular saca el pedido de la
// produccion y su compra deja de contar en la cuenta del cliente, pero no borra
// nada (la compra queda tachada en el estado de cuenta).
//
// Para no anular de casualidad hay una traba: el boton "Habilitar anulacion".
// Mientras este apagado no aparece ningun boton de anular. Solo lo ven los que
// tienen el permiso "pedidos_anular" (o el admin); el endpoint tambien lo exige.
// Editar un confirmado pide el permiso "pedidos_editar": es lo que se le da a
// la encargada para corregir un pedido sin poder tocar precios.

import { api } from "../api";
import { formatearPesos } from "../../lib/money";
import { hoyMontevideo, rangoFechas, ddmmaaaa } from "../../lib/fecha";
import { iconoTacho, iconoLapiz } from "../iconos";
import { editarPedido } from "./pedido";
import type { Sesion } from "../../lib/types";

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

interface PedidoFila {
  id: number;
  fecha_entrega: string;
  total: number;
  estado: string;
  cliente_nombre: string;

  empresa_nombre: string | null;
}

interface ClienteOpcion {
  id: number;
  codigo: string;
  nombre: string;
}

// Respuesta de GET /api/pedidos/:id, para el resumen en pantalla.
interface PedidoDetalle {
  pedido: {
    id: number;
    fecha_entrega: string;
    total: number;
    descuento: number;
    estado: string;
    cliente_nombre: string;

    cliente_direccion: string | null;
    empresa_nombre: string | null;
  };
  lineas: Array<{
    cantidad: number;
    precio_unitario: number;
    subtotal: number;
    codigo: number;
    descripcion: string;
    empaque: number;
  }>;
}

export async function renderPedidos(contenedor: HTMLElement): Promise<void> {
  const hoy = hoyMontevideo();
  const inicioMes = hoy.slice(0, 8) + "01";
  let filas: PedidoFila[] = [];
  let permiteEditar = false;
  let permiteAnular = false;
  // La traba arranca SIEMPRE apagada al entrar a la pantalla.
  let anulacionHabilitada = false;

  contenedor.innerHTML = `
    <div class="mb-4">
      <h1 class="titulo-pagina">Pedidos</h1>
      <p class="subtitulo">Todos los pedidos tomados, con su estado. Desde aca se anulan.</p>
    </div>

    <div class="tarjeta mb-4 flex flex-wrap items-end gap-3">
      <label class="min-w-[14rem] flex-1"><span class="etiqueta">Cliente</span>
        <select id="cliente" class="campo"><option value="">Todos</option></select></label>
      <label><span class="etiqueta">Desde</span><input id="desde" type="date" class="campo" value="${inicioMes}" /></label>
      <label><span class="etiqueta">Hasta</span><input id="hasta" type="date" class="campo" value="${hoy}" /></label>
      <div class="flex gap-1">
        <button class="btn-sec text-sm" data-rango="dia">Dia</button>
        <button class="btn-sec text-sm" data-rango="semana">Semana</button>
        <button class="btn-sec text-sm" data-rango="mes">Mes</button>
        <button class="btn-sec text-sm" data-rango="todo">Todo</button>
      </div>
      <button id="habilitar" class="btn-sec hidden">${iconoTacho} Habilitar anulacion</button>
    </div>

    <div id="avisoAnulacion" class="mb-3 hidden rounded-xl border border-red-300 bg-red-50 px-4 py-2.5 text-sm text-red-800">
      <b>Modo anulacion activo.</b> Los pedidos confirmados muestran su boton Anular.
      Anular saca el pedido de la produccion y su compra deja de contar en la cuenta del cliente
      (no se borra: queda tachada). Volve a tocar el boton para apagarlo.
    </div>

    <div id="salida"></div>
    <dialog id="dlgAnular"></dialog>
    <dialog id="dlgResumen"></dialog>`;

  const selCliente = contenedor.querySelector("#cliente") as HTMLSelectElement;
  const inpDesde = contenedor.querySelector("#desde") as HTMLInputElement;
  const inpHasta = contenedor.querySelector("#hasta") as HTMLInputElement;
  const salida = contenedor.querySelector("#salida") as HTMLElement;
  const btnHabilitar = contenedor.querySelector("#habilitar") as HTMLButtonElement;
  const aviso = contenedor.querySelector("#avisoAnulacion") as HTMLElement;
  const dlg = contenedor.querySelector("#dlgAnular") as HTMLDialogElement;
  const dlgResumen = contenedor.querySelector("#dlgResumen") as HTMLDialogElement;

  // Sesion: editar y anular son permisos separados (el admin tiene los dos).
  try {
    const s = await api.get<Sesion>("/api/sesion");
    const puede = (m: string): boolean => s.es_admin === true || s.permisos.includes(m);
    permiteEditar = puede("pedidos_editar");
    permiteAnular = puede("pedidos_anular");
  } catch {
    permiteEditar = false;
    permiteAnular = false;
  }
  btnHabilitar.classList.toggle("hidden", !permiteAnular);

  try {
    const clientes = await api.get<ClienteOpcion[]>("/api/clientes?activo=1");
    selCliente.innerHTML =
      `<option value="">Todos los clientes</option>` +
      clientes
        .map((c) => `<option value="${c.id}">${esc(c.nombre)}</option>`)
        .join("");
  } catch {
    selCliente.innerHTML = `<option value="">Error al cargar clientes</option>`;
  }

  async function cargar(): Promise<void> {
    const q = new URLSearchParams();
    if (inpDesde.value) q.set("desde", inpDesde.value);
    if (inpHasta.value) q.set("hasta", inpHasta.value);
    if (selCliente.value) q.set("cliente", selCliente.value);
    const qs = q.toString();
    salida.innerHTML = `<div class="tarjeta text-masa-400">Cargando...</div>`;
    try {
      filas = await api.get<PedidoFila[]>(`/api/pedidos${qs ? `?${qs}` : ""}`);
      pintar();
    } catch (e) {
      salida.innerHTML = `<div class="tarjeta text-red-700">${esc(e instanceof Error ? e.message : "Error")}</div>`;
    }
  }

  function pintar(): void {
    if (filas.length === 0) {
      salida.innerHTML = `<div class="tarjeta text-center text-masa-400">No hay pedidos en el periodo elegido.</div>`;
      return;
    }
    const confirmados = filas.filter((p) => p.estado === "CONFIRMADO");
    const totalConfirmado = confirmados.reduce((a, p) => a + p.total, 0);
    const anulados = filas.length - confirmados.length;

    const cuerpo = filas
      .map((p) => {
        const anulado = p.estado === "ANULADO";
        const mostrarAnular = anulacionHabilitada && permiteAnular && !anulado;
        return `<tr class="border-t border-crema-100 ${anulado ? "text-masa-400" : ""}">
          <td class="px-3 py-2 whitespace-nowrap">${ddmmaaaa(p.fecha_entrega)}</td>
          <td class="px-3 py-2"><button class="font-medium text-masa-500 underline decoration-dotted underline-offset-2 hover:text-masa-800" data-ver-resumen="${p.id}" title="Ver el resumen del pedido">#${p.id}</button></td>
          <td class="px-3 py-2 ${anulado ? "line-through" : "font-medium text-masa-800"}">${esc(p.cliente_nombre)}</td>
          <td class="px-3 py-2 text-masa-500">${esc(p.empresa_nombre ?? "—")}</td>
          <td class="px-3 py-2 text-right tabular-nums ${anulado ? "line-through" : ""}">${formatearPesos(p.total)}</td>
          <td class="px-3 py-2">${
            anulado
              ? `<span class="rounded-md bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-700">ANULADO</span>`
              : `<span class="rounded-md bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-green-700">CONFIRMADO</span>`
          }</td>
          <td class="whitespace-nowrap px-3 py-2 text-right">${
            permiteEditar && !anulado
              ? `<button class="chip-accion" data-editar="${p.id}" title="Editar el pedido">${iconoLapiz}</button>`
              : ""
          }${
            mostrarAnular
              ? `<button class="btn-sec ml-1 text-sm" data-anular="${p.id}">${iconoTacho} Anular</button>`
              : ""
          }</td>
        </tr>`;
      })
      .join("");

    salida.innerHTML = `
      <div class="tarjeta overflow-x-auto p-0">
        <table class="w-full min-w-max text-sm">
          <thead class="bg-crema-100 text-[11px] uppercase tracking-wider text-masa-500"><tr>
            <th class="px-3 py-2 text-left font-semibold">Fecha entrega</th>
            <th class="px-3 py-2 text-left font-semibold">Pedido</th>
            <th class="px-3 py-2 text-left font-semibold">Cliente</th>
            <th class="px-3 py-2 text-left font-semibold">Empresa</th>
            <th class="px-3 py-2 text-right font-semibold">Total</th>
            <th class="px-3 py-2 text-left font-semibold">Estado</th>
            <th></th>
          </tr></thead>
          <tbody>${cuerpo}</tbody>
          <tfoot><tr class="border-t-2 border-masa-300 font-semibold">
            <td class="px-3 py-2" colspan="4">
              ${confirmados.length} confirmado${confirmados.length === 1 ? "" : "s"}${anulados ? ` · ${anulados} anulado${anulados === 1 ? "" : "s"}` : ""}
            </td>
            <td class="px-3 py-2 text-right tabular-nums">${formatearPesos(totalConfirmado)}</td>
            <td colspan="2"></td>
          </tr></tfoot>
        </table>
      </div>`;
  }

  /**
   * Resumen del pedido en pantalla: que se pidio, a que precio y cuanto da.
   * A proposito NO es el remito (ese va con logo, dos vias y lugar para la
   * firma, y sale de Produccion): esto es para mirar rapido que lleva el pedido.
   */
  async function verResumen(id: number): Promise<void> {
    dlgResumen.innerHTML = `<div class="p-6 text-masa-400">Cargando resumen...</div>`;
    dlgResumen.showModal();
    try {
      const r = await api.get<PedidoDetalle>(`/api/pedidos/${id}`);
      const p = r.pedido;
      const bultos = r.lineas.reduce((a, l) => a + l.cantidad, 0);
      const filasDet = r.lineas
        .map(
          (l) => `<tr class="border-t border-crema-100">
            <td class="py-1.5 pr-2">${esc(l.descripcion)}
              <span class="text-xs text-masa-400">#${esc(l.codigo)}${l.empaque > 1 ? ` · x${esc(l.empaque)}` : ""}</span></td>
            <td class="py-1.5 pr-2 text-right tabular-nums">${l.cantidad}</td>
            <td class="py-1.5 pr-2 text-right tabular-nums text-masa-500">${formatearPesos(l.precio_unitario)}</td>
            <td class="py-1.5 text-right tabular-nums">${formatearPesos(l.subtotal)}</td>
          </tr>`,
        )
        .join("");
      const anulado = p.estado === "ANULADO";
      dlgResumen.innerHTML = `
        <div class="w-[32rem] max-w-full">
          <div class="flex items-start justify-between gap-3 border-b border-crema-200 p-4">
            <div>
              <h2 class="text-lg font-bold text-masa-900">Resumen del pedido #${p.id}</h2>
              <div class="mt-1 text-sm text-masa-600">
                <div><b>Cliente:</b> ${esc(p.cliente_nombre)}</div>
                <div><b>Entrega:</b> ${ddmmaaaa(p.fecha_entrega)}</div>
                ${p.empresa_nombre ? `<div><b>Empresa:</b> ${esc(p.empresa_nombre)}</div>` : ""}
              </div>
            </div>
            <span class="rounded-md px-2 py-0.5 text-[11px] font-semibold ${anulado ? "bg-red-50 text-red-700" : "bg-green-50 text-green-700"}">${esc(p.estado)}</span>
          </div>
          <div class="max-h-[55vh] overflow-y-auto p-4">
            <table class="w-full text-sm">
              <thead class="text-[11px] uppercase tracking-wider text-masa-400"><tr>
                <th class="pr-2 text-left font-semibold">Articulo</th>
                <th class="pr-2 text-right font-semibold">Cant.</th>
                <th class="pr-2 text-right font-semibold">P. unit</th>
                <th class="text-right font-semibold">Subtotal</th>
              </tr></thead>
              <tbody>${filasDet || `<tr><td colspan="4" class="py-3 text-center text-masa-400">El pedido no tiene articulos.</td></tr>`}</tbody>
            </table>
          </div>
          <div class="border-t border-crema-200 p-4 text-sm">
            <div class="flex justify-between text-masa-500">
              <span>${r.lineas.length} articulo${r.lineas.length === 1 ? "" : "s"} · ${bultos} bulto${bultos === 1 ? "" : "s"}</span>
              ${p.descuento ? `<span>Descuento: ${formatearPesos(p.descuento)}</span>` : ""}
            </div>
            <div class="mt-1 flex items-baseline justify-between">
              <span class="font-semibold text-masa-700">TOTAL</span>
              <span class="text-xl font-bold text-masa-900">${formatearPesos(p.total)}</span>
            </div>
          </div>
          <div class="flex justify-end gap-2 border-t border-crema-200 p-3">
            <button id="r_cerrar" class="btn-sec">Cerrar</button>
          </div>
        </div>`;
      dlgResumen.querySelector("#r_cerrar")?.addEventListener("click", () => dlgResumen.close());
    } catch (e) {
      dlgResumen.innerHTML = `<div class="p-6">
        <p class="text-red-700">${esc(e instanceof Error ? e.message : "Error")}</p>
        <div class="mt-3 text-right"><button id="r_cerrar" class="btn-sec">Cerrar</button></div>
      </div>`;
      dlgResumen.querySelector("#r_cerrar")?.addEventListener("click", () => dlgResumen.close());
    }
  }

  function pedirConfirmacion(p: PedidoFila): void {
    dlg.innerHTML = `
      <div class="w-[26rem] max-w-full p-5">
        <h2 class="mb-1 text-lg font-bold text-masa-900">Anular el pedido #${p.id}?</h2>
        <p class="mb-3 text-sm text-masa-500">Revisa que sea el pedido correcto.</p>
        <div class="mb-4 rounded-xl bg-crema-50 p-3 text-sm">
          <div><b>Cliente:</b> ${esc(p.cliente_nombre)}</div>
          <div><b>Entrega:</b> ${ddmmaaaa(p.fecha_entrega)}</div>
          <div><b>Total:</b> ${formatearPesos(p.total)}</div>
        </div>
        <p class="mb-4 text-xs text-masa-500">
          Sale de la produccion de esa fecha y su compra deja de contar en la cuenta del cliente.
          No se borra: queda tachada en el estado de cuenta.
        </p>
        <p id="a_err" class="mb-2 text-sm text-red-600"></p>
        <div class="flex justify-end gap-2">
          <button id="a_cancelar" class="btn-sec">Cancelar</button>
          <button id="a_ok" class="btn-peligro">${iconoTacho} Anular pedido</button>
        </div>
      </div>`;
    dlg.showModal();
    const err = dlg.querySelector("#a_err") as HTMLElement;
    const btnOk = dlg.querySelector("#a_ok") as HTMLButtonElement;
    dlg.querySelector("#a_cancelar")?.addEventListener("click", () => dlg.close());
    btnOk.addEventListener("click", async () => {
      err.textContent = "";
      btnOk.disabled = true;
      try {
        await api.del(`/api/pedidos/${p.id}`);
        dlg.close();
        await cargar();
      } catch (e) {
        err.textContent = e instanceof Error ? e.message : "No se pudo anular";
        btnOk.disabled = false;
      }
    });
  }

  salida.addEventListener("click", (ev) => {
    const t = ev.target as HTMLElement;
    const ver = t.closest("[data-ver-resumen]") as HTMLElement | null;
    if (ver) {
      void verResumen(Number(ver.dataset.verResumen));
      return;
    }
    const ed = t.closest("[data-editar]") as HTMLElement | null;
    if (ed) {
      editarPedido(Number(ed.dataset.editar));
      return;
    }
    const b = t.closest("[data-anular]") as HTMLElement | null;
    if (!b) return;
    const p = filas.find((f) => f.id === Number(b.dataset.anular));
    if (p) pedirConfirmacion(p);
  });

  btnHabilitar.addEventListener("click", () => {
    anulacionHabilitada = !anulacionHabilitada;
    btnHabilitar.classList.toggle("armado", anulacionHabilitada);
    btnHabilitar.innerHTML = `${iconoTacho} ${anulacionHabilitada ? "Anulacion habilitada" : "Habilitar anulacion"}`;
    aviso.classList.toggle("hidden", !anulacionHabilitada);
    pintar();
  });

  selCliente.addEventListener("change", () => void cargar());
  inpDesde.addEventListener("change", () => void cargar());
  inpHasta.addEventListener("change", () => void cargar());

  contenedor.querySelectorAll<HTMLButtonElement>("[data-rango]").forEach((b) => {
    b.addEventListener("click", () => {
      const tipo = b.dataset.rango as "dia" | "semana" | "mes" | "todo";
      if (tipo === "todo") {
        inpDesde.value = "";
        inpHasta.value = "";
      } else {
        const ref = inpHasta.value || hoy;
        const [d, h] = rangoFechas(ref, tipo);
        inpDesde.value = d;
        inpHasta.value = h;
      }
      void cargar();
    });
  });

  await cargar();
}
